

<template>
  <div>
    <div v-html="$t('ceramicTiles.description')" />
    <div>
      <p>
        <span
          v-t="
            'By standard, you can choose tiles from the Canova (Pietre & Marmi) or The Room (DP) collection. Check out the catalogs:'
          "
        />
      </p>
      <FileChips :items="catalogues" />
    </div>

    <!-- BATHROOMS -->
    <item-block
      v-for="(bathroom, i) in bathrooms"
      :key="'bathroom-' + i"
      :id="'bathroom_' + bathroom.id"
    >
      <base-subheader>
        <span
          v-html="
            $t('Choose ceramic tiles for {0}bathroom', [
              bathroom.size ? `<span class='text-lowercase text-no-wrap'>${bathroom.size} m² </span>` : '',
            ])
          "
        />
      </base-subheader>

      <!-- BATHROOM FLOOR -->
      <div class="mt-5">
        <span class="text-h6" v-t="'Bathroom floor'" />
        <span class="ml-3" v-if="bathroom.floorCeramicTile">
          <v-chip outlined small>
            <v-icon left>mdi-check</v-icon>
            {{ bathroom.floorCeramicTile.collection }}
            {{ bathroom.floorCeramicTile.title }}
          </v-chip>
        </span>
      </div>

      <v-checkbox
        :disabled="$store.getters.isDisabled"
        :input-value="bathroom.floor_120x60"
        @change="onBathroomInput(i, 'floor_120x60', $event)"
        :label="$t('Use 120*60 size tiles instead of standard 60*60 tiles')"
        :hint="
          $t('Extra cost: {0} € per tileable m²', [
            $store.state.options.prices.bathroom.use_120x60_tiles,
          ])
        "
        persistent-hint
      />

      <CeramicTileSelector
        :large-tile="bathroom.floor_120x60"
        :id="`bathrooms.${i}.floor_ceramic_tile_slug`"
        :error="e(`bathrooms.${i}.floor_ceramic_tile_slug`)"
        :error-messages="$t('Please pick ceramic tile {0}', [$t('for bathroom floor')])"
        :items="$store.state.options.ceramic_tiles"
        :value="bathroom.floor_ceramic_tile_slug"
        @input="onBathroomInput(i, 'floor_ceramic_tile_slug', $event)"
      />

      <!-- BATHROOM WALLS -->
      <div class="mt-10">
        <span class="text-h6" v-t="'Bathroom walls'" />
        <span class="ml-3" v-if="bathroom.wallsCeramicTile">
          <v-chip outlined small>
            <v-icon left>mdi-check</v-icon>
            {{ bathroom.wallsCeramicTile.collection }}
            {{ bathroom.wallsCeramicTile.title }}
          </v-chip>
        </span>
      </div>

      <v-checkbox
        :disabled="$store.getters.isDisabled"
        :input-value="bathroom.walls_120x60"
        @change="onBathroomInput(i, 'walls_120x60', $event)"
        :label="$t('Use 120*60 size tiles instead of standard 60*60 tiles')"
        :hint="
          $t('Extra cost: {0} € per tileable m²', [
            $store.state.options.prices.bathroom.use_120x60_tiles,
          ])
        "
        persistent-hint
      />

      <CeramicTileSelector
        :large-tile="bathroom.walls_120x60"
        :id="`bathrooms.${i}.walls_ceramic_tile_slug`"
        :error="e(`bathrooms.${i}.walls_ceramic_tile_slug`)"
        :error-messages="$t('Please pick ceramic tile {0}', [$t('for bathroom walls')])"
        :items="$store.state.options.ceramic_tiles"
        :value="bathroom.walls_ceramic_tile_slug"
        @input="onBathroomInput(i, 'walls_ceramic_tile_slug', $event)"
      />

      <!-- <BathroomWallCeramicTileRadio /> -->

      <v-checkbox
        :disabled="$store.getters.isDisabled"
        :input-value="bathroom.tile_dry_walls"
        @change="onBathroomInput(i, 'tile_dry_walls', $event)"
        :label="$t('Tile all walls (inc. non-wet walls, which are painted by default)')"
        :hint="
          $t('Extra cost: {0} € per tileable m²', [
            $store.state.options.prices.bathroom.tile_dry_walls,
          ])
        "
        persistent-hint
      />

      <v-checkbox
        :disabled="$store.getters.isDisabled"
        :input-value="bathroom.mix_tiles"
        @change="onBathroomInput(i, 'mix_tiles', $event)"
        :label="$t('I would like to mix different tiles')"
      />
      <v-expand-transition>
        <base-textarea
          :id="`bathrooms.${i}.mix_tiles_description`"
          :error="e(`bathrooms.${i}.mix_tiles_description`)"
          :error-messages="[$t('Please describe your preferences')]"
          outlined
          tile
          v-if="bathroom.mix_tiles"
          :value="bathroom.mix_tiles_description"
          @change="onBathroomInput(i, 'mix_tiles_description', $event)"
          :label="$t('Describe your preferences')"
          persistent-hint
          :hint="
            $t(
              'Please note that when using different tiles, both material and labour costs will increase. We will specify the surcharge after reviewing your request.',
            )
          "
        />
      </v-expand-transition>
    </item-block>

    <!-- HALLWAY FLOOR -->
    <item-block id="hallway">
      <base-subheader>
        <span v-html="$t('Choose ceramic tiles for hallway')" />
      </base-subheader>

      <div class="my-5">
        <span class="text-h6" v-t="'Hallway floor'" />
        <span class="ml-3" v-if="$store.getters.hallwayFloorCeramicTile">
          <v-chip outlined small>
            <v-icon left>mdi-check</v-icon>
            {{ $store.getters.hallwayFloorCeramicTile.collection }}
            {{ $store.getters.hallwayFloorCeramicTile.title }}
          </v-chip>
        </span>

        <v-checkbox
          :disabled="$store.getters.isDisabled"
          :input-value="$store.state.preferences.hallway_floor_use_120x60_tiles"
          @change="$store.dispatch('setPreference', { hallway_floor_use_120x60_tiles: $event })"
          :label="$t('Use 120*60 size tiles instead of standard 60*60 tiles')"
          :hint="
            $t('Extra cost: {0} € per tileable m²', [
              $store.state.options.prices.bathroom.use_120x60_tiles,
            ])
          "
          persistent-hint
        />

        <CeramicTileSelector
          :large-tile="$store.state.preferences.hallway_floor_use_120x60_tiles"
          :id="'preferences.hallway_floor_ceramic_tile_slug'"
          :error="e('preferences.hallway_floor_ceramic_tile_slug')"
          :error-messages="$t('Please pick ceramic tile {0}', [$t('for hallway floor')])"
          :items="$store.state.options.ceramic_tiles"
          :value="$store.state.preferences.hallway_floor_ceramic_tile_slug"
          @input="$store.dispatch('setPreference', { hallway_floor_ceramic_tile_slug: $event })"
        />
      </div>
    </item-block>
  </div>
</template>

<script>
import CeramicTileSelector from "../../CeramicTileSelector";
import BathroomWallCeramicTileRadio from "../../BathroomWallCeramicTileRadio";
import FileChips from "../../FileChips";
import MandatorySliderSelector from "@/components/MandatorySliderSelector";
export default {
  components: {
    MandatorySliderSelector,
    FileChips,
    BathroomWallCeramicTileRadio,
    CeramicTileSelector,
  },

  computed: {
    catalogues() {
      return this.$store.state.options.ceramic_tile_catalogues;
    },
    bathrooms() {
      return this.$store.getters.bathrooms;
    },
  },

  data() {
    return {
      errorRegexPatterns: [
        "bathrooms.*.floor_ceramic_tile_slug",
        "bathrooms.*.walls_ceramic_tile_slug",
        "bathrooms.*.mix_tiles_description",
        "preferences.hallway_floor_ceramic_tile_slug",
      ],
    };
  },

  methods: {
    e(key) {
      return this.$store.getters["visibleError"]("CeramicTiles", key);
    },

    onBathroomInput(bathroomIndex, field, value) {
      const updatables = {};
      updatables[field] = value;

      this.$store.dispatch("setBathroom", {
        bathroomIndex,
        updatables,
      });
    },
  },
};
</script>
